import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import css from '@emotion/css';
import { grayScale } from 'app/colors';
import { breakpoints } from 'app/spacing';
import { mobileChatDefaultPadding } from './index';

const getWrapperStyles = (fullScreen: boolean, inModal = false) => {
  if (inModal) {
    return '';
  }

  if (fullScreen) {
    return `
      height: 100vh;
      padding-bottom: 20px;
    `;
  }
  return `
      border-radius: 16px;
      box-shadow: rgba(0, 0, 0, 0.03) 0px 18px 26px;
      padding-bottom: 20px;
    `;
};

export const ChatWrapper = styled.div<{
  fullScreen: boolean;
  inModal?: boolean;
}>`
  width: 100%;
  background-color: ${grayScale.white};
  ${({ fullScreen, inModal }) => getWrapperStyles(fullScreen, inModal)};
  position: relative;
`;

function getPaddingBottom(
  isMobile: boolean,
  isAutoResponsesDisplayed: boolean
) {
  if (isMobile && isAutoResponsesDisplayed) {
    return '250px';
  } else if (isAutoResponsesDisplayed) {
    return '100px';
  } else {
    return '20px';
  }
}

export const MessageHistoryWrapper = styled.div<{
  loading: boolean;
  isAutoResponsesDisplayed: boolean;
  isMobile: boolean;
  fullHeight: boolean;
  sidePadding: string;
  topPadding: string;
  shrinkHeightAmount?: number;
}>`
  margin-top: 1px;
  width: 100%;

  height: ${({ fullHeight, shrinkHeightAmount = 0 }) =>
    fullHeight
      ? `calc(100vh - ${131 + shrinkHeightAmount}px)`
      : `calc(100vh - ${428 + shrinkHeightAmount}px)`};
  overflow-x: scroll;
  display: flex;
  flex-direction: column;
  gap: 25px;
  ${({ sidePadding }) => sidePadding};
  ${({ fullHeight }) =>
    fullHeight
      ? ''
      : css`
          min-height: 239px;
        `} // increases total chat height to 400px
  padding-bottom: ${({ isMobile, isAutoResponsesDisplayed }) =>
    getPaddingBottom(isMobile, isAutoResponsesDisplayed)};
  padding-top: ${({ topPadding, fullHeight }) =>
    topPadding ? topPadding : fullHeight ? '0' : '25'}px;
  @media screen and (min-width: ${breakpoints.lg}px) and (max-width: ${breakpoints.lg +
    50}px) {
    padding-bottom: 140px;
  }
  justify-content: ${({ loading }) => (loading ? 'center' : 'flex-start')};
`;

export const MessageWrapper = styled.div<{ alignment: string }>`
  display: flex;
  ${({ alignment }) => css`
    justify-content: ${alignment === 'right' ? 'flex-end' : 'flex-start'};
  `}
`;

export const GradientTopWrapper = styled.div<{
  borderRadius: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 10px;
  border-radius: ${({ borderRadius }) => borderRadius};
`;

export const GradientBottomWrapper = styled.div<{
  topPosition: string;
}>`
  position: absolute;
  top: ${({ topPosition }) => topPosition};
  left: 0;
  width: 100%;
  height: 10px;
`;

export const Message = styled.div<{
  isSender: boolean;
  color: string;
}>`
  ${({ color }) => css`
    background-color: ${color};
  `}
  color: white;
  border-radius: 10px;
  padding: 10px;
  max-width: 65%;
  white-space: pre-wrap;
  border: 1px solid
    ${({ isSender }) =>
      isSender ? 'var(--color-blue-300)' : 'var(--color-teal-300)'};
`;

export const MessageTop = styled.div`
  width: 100%;
  padding-bottom: 10px;
  padding-top: 5px;
  padding-left: 5px;
`;

export const MessageBottom = styled.div`
  padding: 0 10px 15px 10px;
`;

export const MesssageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 10px;
  color: var(--color-gray-800);
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  padding-top: 10px;
`;

export const FloatingResponsesWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const Responses = styled.div<{
  isMobile: boolean;
  isOdd: boolean;
  sidePadding: string;
}>`
  display: flex;
  grid-template-columns: ${({ isOdd }) => (isOdd ? '1fr 1fr' : '1fr')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};

  justify-content: center;
  align-items: center;
  gap: ${({ isMobile }) => (isMobile ? '5px' : '10px')};
  position: absolute;
  transform: translateX(-50%);
  width: 100%;
  max-height: 60px;

  ${({ isMobile, sidePadding }) =>
    isMobile ? mobileChatDefaultPadding : sidePadding};

  left: 50%;
  @media screen and (min-width: ${breakpoints.lg}px) and (max-width: ${breakpoints.lg +
    50}px) {
    top: ${({ isMobile }) => (isMobile ? '-120px' : '-100px')};
  }
  animation: fadeIn ease 0.5s;
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  button {
    max-hegiht: 80px;
    background-color: white;
    border: 1px solid var(--color-blue-500);
    border-radius: 8px;
    color: var(--color-blue-500);
    text-align: center;
    text-decoration: none;
    display: inline-block;
    font-size: 16px;
    margin: 4px 2px;
    padding: 13px 15px 13px 15px;
    cursor: pointer;
    box-shadow: 0 18px 26px rgba(0, 0, 0, 0.03);
  }
  button:active {
    outline: none;
  }
  button:focus {
    outline: none;
  }
  button:hover {
    background-color: var(--color-blue-500);
    color: white;
  }
  button:active {
    background-color: var(--color-blue-600);
    color: white;
  }
  button:disabled {
    border: 1px solid var(--color-gray-600);
    color: var(--color-gray-600);
    background-color: white;
    cursor: auto;
  }
`;

export const MessageInput = styled.div<{ chatPadding: string }>`
  width: 100%;
  ${({ chatPadding }) => chatPadding}
`;

export const ResponseLoadingWrapper = styled.div<{ inline?: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: 3px;
  ${({ inline }) =>
    inline
      ? ''
      : css`
          width: 100%;
        `}
`;

export const LoadingDotsWrapper = styled.div`
  display: flex;
  justifycontent: center;
  padding-bottom: 1px;
`;

const jump = keyframes`
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-2px); }
`;

export const Dot = styled.div<{ color: string }>`
  background-color: var(--color-${({ color }) => color});
  border-radius: 50%;
  width: 2px;
  height: 2px;
  margin: 0 2px;
  animation: ${jump} 1s infinite;
`;

export const ControlWrapper = styled.div<{
  chatPadding: string;
  isMobile: boolean;
}>`
  ${({ chatPadding }) => chatPadding};
  display: flex;
  justify-content: end;
  padding-top: 10px;
  gap: 5px 15px;
  align-items: center;
  ${({ isMobile }) => isMobile && 'flex-wrap: wrap;'}
  min-width: 250px;
`;

export const ScrollToBottom = styled.div<{ higher: boolean }>`
  position: absolute;
  bottom: ${({ higher }) => (higher ? '180px' : '150px')};
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  display: flex;
  justify-content: center;
`;

export const Error = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 10%;
  display: flex;
  padding-top: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;
